import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtTime, fmtErr } from "../api";
import { StatusBadge } from "../pages/Attendance";

const AttendanceAdmin = () => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [date, setDate] = useState(todayStr);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null); // {id, name, rows, loading, error}

  const load = async (d) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/attendance/all", { params: { date: d || undefined, limit: 300 } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(todayStr); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDrill = async (r) => {
    setDrill({ id: r.user_id, name: r.user_name, rows: [], loading: true, error: "" });
    try {
      const { data } = await staffApi.get(`/attendance/user/${r.user_id}`, { params: { limit: 30 } });
      setDrill({ id: r.user_id, name: r.user_name, rows: Array.isArray(data) ? data : [], loading: false, error: "" });
    } catch (e) {
      setDrill({ id: r.user_id, name: r.user_name, rows: [], loading: false, error: fmtErr(e) });
    }
  };

  return (
    <div data-testid="admin-attendance">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">All Employee Attendance</h1>
          <p className="mt-1 text-sm text-slate-500">Read-only view. Click an employee for their history.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} data-testid="admin-att-date" onChange={(e) => setDate(e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
          <button onClick={() => load(date)} data-testid="admin-att-load"
            className="bg-[#0A1128] hover:bg-[#16204a] text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
            View
          </button>
        </div>
      </div>
      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="admin-att-error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="admin-att-empty">No attendance records for this date.</p>
      ) : (
        <div className="mt-6 bg-white border border-slate-200 overflow-x-auto" data-testid="admin-att-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Employee", "Date", "Status", "In", "Out", "Hours", "Late", "Branch"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDrill(r)} data-testid={`admin-att-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-[#C5221F]">{r.user_name}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{fmtTime(r.check_in_time)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtTime(r.check_out_time)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.total_hours != null ? `${r.total_hours} hrs` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.late_by_min ? `${r.late_by_min} min` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.branch_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="att-drill-modal" onClick={() => setDrill(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">{drill.name} — History</h2>
              <button onClick={() => setDrill(null)} data-testid="att-drill-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            {drill.loading ? (
              <p className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
            ) : drill.error ? (
              <p className="mt-4 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm">{drill.error}</p>
            ) : drill.rows.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500">No records.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-100" data-testid="att-drill-list">
                {drill.rows.map((r) => (
                  <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm flex-wrap">
                    <span className="font-medium text-slate-800">{fmtDate(r.date)}</span>
                    <span className="text-xs text-slate-500">{fmtTime(r.check_in_time)} – {fmtTime(r.check_out_time)}</span>
                    <StatusBadge value={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAdmin;
